/**
 * Custom hook for managing document source connection form state
 * Handles form data initialization and updates
 */

import { useState } from 'react';
import { DocumentSourceUserConnectionRequest } from '@/api/ApiClient';
import { ExtendedConnectionResponse } from '@/types/common';

type FormDataType = Omit<DocumentSourceUserConnectionRequest, 'provider_config_id'> & { provider_config_id: string };

const DEFAULT_FORM_DATA: FormDataType = {
  provider_config_id: '',
  name: '',
  enabled: true,
  sync_interval: 'daily',
  file_filters: ['*.pdf', '*.docx', '*.txt'],
  max_file_size: 50 * 1024 * 1024, // 50MB
  retry_attempts: 5,
  retry_delay: 60,
  debounce_seconds: 10,
  folder_path: '/',
  share_url: '',
};

interface UseConnectionFormStateReturn {
  formData: FormDataType;
  resetForm: () => void;
  populateForm: (connection: ExtendedConnectionResponse) => void;
  updateFormData: (changes: Partial<FormDataType>) => void;
}

export const useConnectionFormState = (): UseConnectionFormStateReturn => {
  const [formData, setFormData] = useState<FormDataType>(DEFAULT_FORM_DATA);

  const resetForm = (): void => {
    setFormData(DEFAULT_FORM_DATA);
  };

  const populateForm = (connection: ExtendedConnectionResponse): void => {
    setFormData({
      provider_config_id: connection.provider_config_id,
      name: connection.name,
      enabled: connection.enabled,
      sync_interval: connection.sync_interval,
      file_filters: connection.file_filters ?? ['*.pdf', '*.docx', '*.txt'],
      max_file_size: 50 * 1024 * 1024,
      retry_attempts: 5,
      retry_delay: 60,
      debounce_seconds: 10,
      folder_path: connection.folder_path ?? '/',
      share_url: connection.share_url ?? '',
    });
  };

  const updateFormData = (changes: Partial<FormDataType>): void => {
    setFormData(prev => ({ ...prev, ...changes }));
  };

  return {
    formData,
    resetForm,
    populateForm,
    updateFormData,
  };
};